import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';
import { createClientSidePaginatedLoader } from '@shared/utils/paginated-field.helpers';

export interface VietnamProvince {
  id: number;
  name: string;
  code: string;
  full_name: string;
  name_en: string;
  full_name_en: string;
}

export interface VietnamWard {
  id: number;
  name: string;
  code: string;
  full_name: string;
  name_en: string;
  full_name_en: string;
  province_id: number;
}

export interface VietnamWardsResponse {
  province_id: number;
  wards: VietnamWard[];
}

export interface ProvinceToWardsMap {
  [provinceId: number]: VietnamWard[];
}

const VIETNAME_WARDS_URL = '/assets/data/vietnam-wards.json';
const VIETNAME_PROVINCES_URL = '/assets/data/vietnam-provinces.json';

@Injectable({
  providedIn: 'root',
})
export class VietnamAddressService {
  private provinces: VietnamProvince[] = [];
  private wards: ProvinceToWardsMap = {};
  private wardsLoaded$: Observable<ProvinceToWardsMap> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Load provinces from JSON file
   */
  getProvinceOptionsPage(
    params: PaginatedDropdownLoadParams,
    lang = 'vi',
  ): Observable<PaginatedOptionsPage<number>> {
    return createClientSidePaginatedLoader<number>(() =>
      this.loadProvinces().pipe(
        map((provinces) =>
          provinces.map((p) => ({
            label: lang === 'en' ? p.full_name_en : p.full_name,
            value: p.id,
          })),
        ),
      ),
    )(params);
  }

  loadProvinces(): Observable<VietnamProvince[]> {
    if (this.provinces.length > 0) {
      return of(this.provinces);
    }

    return this.http.get<VietnamProvince[]>(VIETNAME_PROVINCES_URL).pipe(
      map((provinces) => {
        this.provinces = provinces;
        return provinces;
      }),
    );
  }

  /**
   * Load wards for a specific province
   */
  getWardOptionsPage(
    provinceId: number | null | undefined,
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    if (!provinceId) {
      return of({ options: [], pageContentLength: 0 });
    }

    return this.loadWardsByProvince(provinceId).pipe(
      switchMap((wards) => {
        const options = wards.map((ward) => ({
          label: ward.full_name,
          value: ward.id,
        }));
        return createClientSidePaginatedLoader<number>(() => of(options))(
          params,
        );
      }),
    );
  }

  loadWardsByProvince(provinceId: number): Observable<VietnamWard[]> {
    // If wards are already loaded, return immediately
    if (Object.keys(this.wards).length > 0) {
      return of(this.wards[provinceId] || []);
    }

    // Load all wards from JSON file once and share for all subscribers
    if (!this.wardsLoaded$) {
      this.wardsLoaded$ = this.http
        .get<VietnamWardsResponse[]>(VIETNAME_WARDS_URL, {
          cache: 'force-cache',
        })
        .pipe(
          map((wardsResponse) => {
            // Map to province-based structure
            this.wards = this.toMap(wardsResponse);
            return this.wards;
          }),
          shareReplay(1), // Cache the HTTP request result and share for all subscribers
        );
    }

    // Return the specific province's wards from the shared Observable
    return this.wardsLoaded$.pipe(
      map((wardsMap) => wardsMap[provinceId] || []),
    );
  }

  /**
   * Get all provinces (synchronous)
   */
  getProvinces(): VietnamProvince[] {
    return this.provinces;
  }

  /**
   * Get wards for a province (synchronous)
   */
  getWardsByProvince(provinceId: string | number): VietnamWard[] {
    const numericProvinceId =
      typeof provinceId === 'string' ? parseInt(provinceId) : provinceId;
    return this.wards[numericProvinceId] || [];
  }

  /**
   * Get province by ID
   */
  getProvinceById(provinceId: number): VietnamProvince | undefined {
    return this.provinces.find((p) => p.id === provinceId);
  }

  /**
   * Get ward by ID
   */
  getWardById(provinceId: number, wardId: number): VietnamWard | undefined {
    const wards = this.wards[provinceId] || [];
    return wards.find((w) => w.id === wardId);
  }

  /**
   * Find ward by ID across all provinces
   */
  findWardById(wardId: number): Promise<VietnamWard | undefined> {
    return new Promise((resolve) => {
      const searchWard = (): VietnamWard | undefined => {
        for (const list of Object.values(this.wards)) {
          const found = list.find((w: VietnamWard) => w.id === wardId);
          if (found) return found;
        }
        return undefined;
      };

      if (Object.keys(this.wards).length > 0) {
        resolve(searchWard());
        return;
      }

      // Use loadWardsByProvince to trigger shared request
      this.loadWardsByProvince(1).subscribe({
        next: () => resolve(searchWard()),
        error: () => resolve(undefined),
      });
    });
  }

  private toMap(wards: VietnamWardsResponse[]): ProvinceToWardsMap {
    return wards.reduce((acc, curr) => {
      acc[curr.province_id] = curr.wards;
      return acc;
    }, {} as ProvinceToWardsMap);
  }
}
